import React, { useEffect, useState, Fragment } from "react";
import { connect } from "react-redux";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Transition } from "@headlessui/react";
import { IconHome, IconDoorExit, IconMenu2, IconX } from "@tabler/icons-react";
import {
  check_authenticated,
  load_user,
  logout,
  refresh,
} from "../../redux/actions/auth/auth";
import logoimg from "../../assets/img/icon.png";
import Toast from "../../components/Alert";

function DashboardNavbar({
  refresh,
  check_authenticated,
  load_user,
  user,
  logout,
  isAuthenticated,
  onRequestLogout,
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Cierra menú al cambiar de ruta
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Carga/valida sesión
  useEffect(() => {
    if (isAuthenticated) {
      refresh();
      check_authenticated();
      load_user();
    }
  }, [isAuthenticated, refresh, check_authenticated, load_user]);

  // Cerrar con ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setIsMobileMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const navigation = [
    { name: "Dashboard", href: "/dashboard", icon: IconHome },
    // Agrega más items aquí si lo necesitas
  ];

  const doLogout = () => {
    if (onRequestLogout) {
      onRequestLogout(); // abre modal del layout
      return;
    }
    // fallback: logout inmediato
    logout();
    Toast({ tipo: "success", mensaje: "¡Sesión cerrada correctamente!" });
    navigate("/", { replace: true });
    // window.location.reload(); // opcional si necesitas limpiar estado global
  };

  const NavigationLink = ({ item, isMobile = false }) => (
    <NavLink
      key={item.name}
      to={item.href}
      onClick={() => isMobile && setIsMobileMenuOpen(false)}
      className={({ isActive }) =>
        `group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all ${
          isMobile
            ? isActive
              ? "bg-gray-100 text-gray-900"
              : "text-gray-700 hover:bg-gray-100"
            : isActive
            ? "bg-white/15 text-white shadow-sm"
            : "text-white/80 hover:text-white hover:bg-white/10"
        }`
      }
    >
      <item.icon className="mr-2 h-5 w-5" aria-hidden="true" />
      {item.name}
    </NavLink>
  );

  const LogoutButton = ({ isMobile = false }) => (
    <button
      onClick={doLogout}
      className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all ${
        isMobile
          ? "text-red-600 hover:bg-red-50 hover:text-red-700 w-full"
          : "text-white/90 hover:text-white hover:bg-white/10"
      }`}
    >
      <IconDoorExit className="mr-2 h-5 w-5" aria-hidden="true" />
      Cerrar Sesión
    </button>
  );

  return (
    <nav className="sticky top-0 z-50 bg-gradient-to-r from-[#ff1049] to-[#ff7826] shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Barra principal */}
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/dashboard" className="flex items-center space-x-2 rounded-full">
              <img src={logoimg} className="h-9 w-9 rounded-full" alt="TableChat Logo" />
              <span className="hidden sm:block text-lg font-bold text-white">
                TableChat
              </span>
            </Link>
          </div>

          {/* Desktop */}
          {isAuthenticated && (
            <div className="hidden md:flex items-center space-x-2">
              {navigation.map((item) => (
                <NavigationLink key={item.name} item={item} />
              ))}

              <div className="ml-4 pl-4 border-l border-white/20">
                <div className="flex items-center space-x-3">
                  {user && (
                    <span className="text-sm text-white/90">
                      {user.first_name} {user.last_name}
                    </span>
                  )}
                  <LogoutButton />
                </div>
              </div>
            </div>
          )}

          {/* Botón mobile */}
          {isAuthenticated && (
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen((v) => !v)}
                className="inline-flex items-center justify-center p-2 rounded-md text-white/90 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40 transition"
                aria-expanded={isMobileMenuOpen}
                aria-label={isMobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
              >
                {isMobileMenuOpen ? <IconX className="h-6 w-6" /> : <IconMenu2 className="h-6 w-6" />}
              </button>
            </div>
          )}
        </div>

        {/* Overlay mobile */}
        <Transition
          show={isMobileMenuOpen}
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 -translate-y-4"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 -translate-y-4"
        >
          <div className="md:hidden relative">
            {/* Panel */}
            <div className="mt-2 mb-4 rounded-xl overflow-hidden shadow-xl ring-1 ring-black/5 bg-white">
              {/* Header del menú */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-900">MENÚ</span>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-md text-gray-700 hover:bg-gray-100"
                  aria-label="Cerrar menú"
                >
                  <IconX className="h-5 w-5" />
                </button>
              </div>

              {/* Info usuario */}
              {user && (
                <div className="px-4 py-2 text-sm text-gray-600 border-b border-gray-100">
                  {user.first_name} {user.last_name}
                </div>
              )}

              {/* Links */}
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navigation.map((item) => (
                  <NavigationLink key={item.name} item={item} isMobile />
                ))}
              </div>

              {/* Logout */}
              <div className="px-2 pt-2 pb-3 border-t border-gray-100">
                <LogoutButton isMobile />
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </nav>
  );
}

const mapStateToProps = (state) => ({
  user_loading: state.auth.user_loading,
  user: state.auth.user,
  isAuthenticated: state.auth.isAuthenticated,
});

export default connect(mapStateToProps, {
  refresh,
  check_authenticated,
  load_user,
  logout,
})(DashboardNavbar);
